import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { authApi } from '../services/api/auth';
import { matches, passwordComplexity } from '../utils/validators';

type ResetPasswordFieldErrors = {
  password: string[];
  passwordConfirmation: string[];
};

export const useResetPassword = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [success, setSuccess] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<ResetPasswordFieldErrors>({
    password: [],
    passwordConfirmation: [],
  });
  const [formErrors, setFormErrors] = useState<string[]>([]);

  const token = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('token') || '';
  }, [location.search]);

  const validate = () => {
    const nextFieldErrors: ResetPasswordFieldErrors = {
      password: [],
      passwordConfirmation: [],
    };
    const nextFormErrors: string[] = [];

    const passwordError = passwordComplexity(password, 'auth.reset_password.validation.password_complexity');
    if (passwordError) {
      nextFieldErrors.password.push(passwordError);
      nextFormErrors.push(passwordError);
    }

    const mismatchError = matches(
      password,
      passwordConfirmation,
      'auth.reset_password.validation.password_mismatch',
    );
    if (mismatchError) {
      nextFieldErrors.passwordConfirmation.push(mismatchError);
      nextFormErrors.push(mismatchError);
    }

    setFieldErrors(nextFieldErrors);
    setFormErrors(nextFormErrors);
    setSuccess(false);

    return nextFieldErrors;
  };

  const mutation = useMutation({
    mutationFn: () => authApi.resetPassword(token, password, passwordConfirmation),
    onSuccess: () => {
      setSuccess(true);
      setFieldErrors({
        password: [],
        passwordConfirmation: [],
      });
      setFormErrors([]);
      setTimeout(() => navigate('/login'), 1200);
    },
    onError: () => {
      setSuccess(false);
      setFormErrors(['auth.reset_password.error']);
    },
  });

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    if (!token) {
      setFormErrors(['auth.reset_password.missing_token']);
      return;
    }

    const nextFieldErrors = validate();

    if (nextFieldErrors.password.length > 0 || nextFieldErrors.passwordConfirmation.length > 0) {
      return;
    }

    setSuccess(false);
    mutation.mutate();
  };

  return {
    password,
    setPassword,
    passwordConfirmation,
    setPasswordConfirmation,
    fieldErrors,
    formErrors,
    validate,
    handleSubmit,
    loading: mutation.isPending,
    success,
  };
};
